'use client'
import { useQuery } from '@apollo/client'
import { envs } from '@config/envs'
import { GET_PROFILE, setUser } from '@data/reactive-variables/profile'
import {
  getHavePermissionGroup,
  getUserPermission,
  hasSupervisorPermission,
  UserPermission,
} from '@utils/permissions'
import { safeLocalStorage } from '@utils/utilities'
import dayjs from 'dayjs'
import { useState } from 'react'

import { getAxiosInstance } from '../../../axios/axios-instance'
import { hermesLogger } from '../../../observability'
import { hubHandler } from '../../utils'
import { useGetHubsList } from '../use-get-hubs-list/use-get-hubs-list.hook'
import { UseProfileHookResponse, User } from './use-profile.hook.types'

const USER_PROFILE_VALID_TIME = 4 * 3600
const DEV_PERMISSION_OVERRIDE_KEY = envs.devPermissionOverrideKey
const AUTH_API_PERMISSION_KEY = envs.authApiPermissionKey

const applyDevPermission = (userData: User): User => {
  const devPermissionOverride = safeLocalStorage.getItem(
    DEV_PERMISSION_OVERRIDE_KEY
  )
  const realPermissions = JSON.parse(
    safeLocalStorage.getItem(AUTH_API_PERMISSION_KEY) || '[]'
  )

  if (devPermissionOverride && realPermissions.includes(UserPermission.SA)) {
    return {
      ...userData,
      permissions: [JSON.parse(devPermissionOverride).user.permissions[0]],
    }
  }
  return userData
}

const saveUserProfileToStorage = (userData: User) => {
  safeLocalStorage.setItem(
    DEV_PERMISSION_OVERRIDE_KEY,
    JSON.stringify({
      user: userData,
      timeStamp: new Date().toISOString(),
    })
  )
}

const saveRealPermissionToStorage = (userData: User) => {
  const realPermissions = userData.permissions || [UserPermission.unknown]
  safeLocalStorage.setItem(
    AUTH_API_PERMISSION_KEY,
    JSON.stringify(realPermissions)
  )
}

export default function useProfile(): UseProfileHookResponse {
  const [isLoading, setIsLoading] = useState(false)
  const { data: profileData, loading: profileLoading } = useQuery(GET_PROFILE)
  const { fetchHubs } = useGetHubsList()
  const { setDefaultHub, getHubsList } = hubHandler()
  const _user = profileData?.user

  const buildPermissionsQuery = () => {
    return Object.values(UserPermission).join(',')
  }

  const fetchUserProfile = async () => {
    const userProfileStorage = safeLocalStorage.getItem(
      DEV_PERMISSION_OVERRIDE_KEY
    )
    const userProfileCache = userProfileStorage
      ? JSON.parse(userProfileStorage)
      : null
    const isExpiredUserProfile = userProfileCache
      ? dayjs().diff(dayjs(userProfileCache?.timestamp)) >
        USER_PROFILE_VALID_TIME
      : true

    if (!userProfileCache || isExpiredUserProfile) {
      try {
        const permissions = buildPermissionsQuery()
        const axiosInstance = getAxiosInstance()

        setIsLoading(true)
        const response = await axiosInstance.get<User>(
          `/auth/me?permissions=${permissions}`
        )

        saveRealPermissionToStorage(response.data)

        const userData = applyDevPermission(response.data)

        setUser(userData)
        saveUserProfileToStorage(userData)

        const userHasPermission = getUserPermission(userData)
        if (!userHasPermission) {
          throw `the user ${userData.email} does not have access permission`
        }

        await fetchHubs()
        setIsLoading(false)
      } catch (error) {
        hermesLogger.error(
          '[useProfile] Error fetching user profile from Cerberus Auth Service',
          {
            error,
          }
        )
      }
    } else if (userProfileCache && !_user) {
      const cachedUserData = applyDevPermission(userProfileCache.user)
      setUser(cachedUserData)

      if (getHubsList()) {
        setDefaultHub()
      } else {
        await fetchHubs()
      }
    }
  }

  return {
    user: _user,
    isLoading: isLoading || profileLoading || !_user,
    userPermission: getUserPermission(_user),
    havePermissionGroup: getHavePermissionGroup(_user),
    isSupervisor: hasSupervisorPermission(_user),
    fetchUserProfile,
  }
}
